import { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format } from "date-fns";
import { CalendarIcon } from "lucide-react";
import moment from "moment";
import { toast } from "sonner";
import { daily_work_status } from "@/models/daily-work.model";
import { updateDailyWork } from "@/app/vendors/daily-work/daily-work.api";
import { DailyWorkJoin } from "@/repositories/daily-work.repository";
import { DailyWorkScheduleRepository } from "@/repositories/daily-work-schedule.repository";
import {
  formatMonthlyPriceInput,
  MonthlyPriceField,
  parseMonthlyPrice,
} from "@/app/vendors/daily-work/components/monthly-price-field";

interface UpdateDailyWorkModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dailyWork: DailyWorkJoin;
  onSuccess: () => void;
}

export default function UpdateDailyWorkModal({
  open,
  onOpenChange,
  dailyWork,
  onSuccess,
}: UpdateDailyWorkModalProps) {
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<daily_work_status>(dailyWork.status);
  const [startDate, setStartDate] = useState<Date | undefined>(
    dailyWork.start_date ? moment(dailyWork.start_date).toDate() : undefined
  );
  const [endDate, setEndDate] = useState<Date | undefined>(
    dailyWork.end_date ? moment(dailyWork.end_date).toDate() : undefined
  );
  const [monthlyPrice, setMonthlyPrice] = useState(formatMonthlyPriceInput(dailyWork.monthly_price));
  const [startDateOpen, setStartDateOpen] = useState(false);
  const [endDateOpen, setEndDateOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) {
      setStatus(dailyWork.status);
      setStartDate(dailyWork.start_date ? moment(dailyWork.start_date).toDate() : undefined);
      setEndDate(dailyWork.end_date ? moment(dailyWork.end_date).toDate() : undefined);
      setMonthlyPrice(formatMonthlyPriceInput(dailyWork.monthly_price));
    }
  }, [open, dailyWork]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const nextStartDate = startDate ? format(startDate, "yyyy-MM-dd") : null;
    const nextEndDate = endDate ? format(endDate, "yyyy-MM-dd") : null;
    const nextMonthlyPrice = parseMonthlyPrice(monthlyPrice);
    if (nextMonthlyPrice === undefined) {
      toast.error("Monthly price must be a non-negative amount");
      return;
    }
    // Billing settings (monthly price) never affect generated days, so they
    // deliberately do not trigger a master-schedule re-sync below.
    const scheduleInputsChanged =
      status !== dailyWork.status ||
      nextStartDate !== (dailyWork.start_date ?? null) ||
      nextEndDate !== (dailyWork.end_date ?? null);

    // Cancelling or pulling the end date in immediately deletes the future
    // generated (not yet accepted/paid) days from the master schedule.
    const cancelling = status === "cancelled" && dailyWork.status !== "cancelled";
    const shortening = Boolean(nextEndDate) && (!dailyWork.end_date || nextEndDate < dailyWork.end_date);
    if (
      (cancelling || shortening) &&
      !window.confirm(
        cancelling
          ? "Cancelling this contract removes all of its upcoming master-schedule days. Continue?"
          : `Ending this contract on ${nextEndDate} removes the upcoming master-schedule days after that date. Continue?`
      )
    ) {
      return;
    }

    setLoading(true);
    try {
      await updateDailyWork(dailyWork.id, {
        status,
        start_date: nextStartDate,
        end_date: nextEndDate,
        monthly_price: nextMonthlyPrice,
      });
      toast.success("Property contract updated successfully");

      // Status and dates drive the master-schedule window (paused freezes,
      // cancelled/shortened prunes, resumed refills). Reconcile now rather than
      // leaving stale generated days live until the nightly sync.
      if (scheduleInputsChanged) {
        try {
          const sync = await DailyWorkScheduleRepository.syncSchedule(dailyWork.id);
          const touched = sync.inserted + sync.updated + sync.deleted;
          if (sync.frozen) {
            toast.info("Contract paused — generated days are frozen until it is active again");
          } else if (touched > 0) {
            toast.info(
              `Master schedule reconciled: ${sync.inserted} added, ${sync.updated} updated, ${sync.deleted} removed`
            );
          }
        } catch (syncError: any) {
          toast.error(syncError.message || "Contract saved, but the master schedule could not be reconciled");
        }
      }

      onSuccess();
    } catch (error: any) {
      toast.error(error.message || "Failed to update property contract");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Update Property Contract</DialogTitle>
          <DialogDescription>Update the Daily Work contract for {dailyWork.property.name}</DialogDescription>
        </DialogHeader>
        <div ref={containerRef}>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Property</Label>
              <Input value={dailyWork.property.name} disabled className="w-full" />
            </div>

            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select value={status} onValueChange={(value) => setStatus(value as daily_work_status)}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={daily_work_status.active}>Active</SelectItem>
                  <SelectItem value={daily_work_status.paused}>Paused</SelectItem>
                  <SelectItem value={daily_work_status.cancelled}>Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Popover open={startDateOpen} onOpenChange={setStartDateOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {startDate ? format(startDate, "PPP") : <span>Pick a date</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start" container={containerRef.current}>
                    <Calendar mode="single" selected={startDate} onSelect={setStartDate} initialFocus />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-2">
                <Label>End Date</Label>
                <Popover open={endDateOpen} onOpenChange={setEndDateOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {endDate ? format(endDate, "PPP") : <span>Pick a date</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start" container={containerRef.current}>
                    <Calendar mode="single" selected={endDate} onSelect={setEndDate} initialFocus />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
            <MonthlyPriceField value={monthlyPrice} onChange={setMonthlyPrice} />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? "Updating..." : "Update"}
            </Button>
          </DialogFooter>
        </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
